import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { documentApiService } from '../services/documentApi';
import { getCurrentUserId } from '../../../utils/userUtils';

export const useDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const userId = getCurrentUserId();
      const fetchedDocuments = await documentApiService.fetchDocuments(userId);
      setDocuments(fetchedDocuments);
      setError('');
    } catch (error) {
      console.error('Error fetching documents', error);
      message.error('Error fetching document list.');
      setDocuments([]);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadDocument = useCallback(async (file, uploadFileName, uploadCompany, uploadType) => {
    try {
      const result = await documentApiService.uploadDocument(file, uploadFileName, uploadCompany, uploadType);
      message.success(result.message || 'File uploaded successfully.');
      await fetchDocuments();
      return { success: true };
    } catch (error) {
      console.error('Error uploading file', error);
      message.error(error.response?.data?.detail || 'Error uploading file. Please try again.');
      return { success: false, error };
    }
  }, [fetchDocuments]);

  const deleteDocument = useCallback(async (documentName) => {
    try {
      await documentApiService.deleteDocument(documentName);
      message.success('File deleted successfully.');
      await fetchDocuments();
      return { success: true };
    } catch (error) {
      console.error('Error deleting file:', error);
      const errorMessage = error.response?.data?.detail || 'Error deleting file. Please try again.';
      message.error(errorMessage);
      return { success: false, error };
    }
  }, [fetchDocuments]);

  const downloadDocument = useCallback(async (document) => {
    try {
      await documentApiService.downloadDocument(document);
      message.success('Download started successfully.');
      return { success: true };
    } catch (error) {
      console.error('Error downloading file:', error);
      message.error('Failed to download the file. Please try again.');
      return { success: false, error };
    }
  }, []);

  const updateDocumentMetadata = useCallback(async (documentName, newFileName, companyName, documentType) => {
    try {
      await documentApiService.updateDocumentMetadata(documentName, newFileName, companyName, documentType);
      message.success('Document details updated successfully');
      await fetchDocuments();
      return { success: true };
    } catch (error) {
      console.error('Error updating document metadata:', error);
      message.error('Failed to update document details');
      return { success: false, error };
    }
  }, [fetchDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    downloadDocument,
    updateDocumentMetadata,
  };
};